import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { IdentificationCard } from "@phosphor-icons/react";

export const StudentLoginCard = () => {
  const { studentLogin, setUser } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [stuFirst, setStuFirst] = useState("");
  const [stuLast, setStuLast] = useState("");
  const [stuId, setStuId] = useState("");
  const [stuLoading, setStuLoading] = useState(false);
  const [stuErr, setStuErr] = useState("");
  const [reveal, setReveal] = useState(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotLast, setForgotLast] = useState("");
  const [forgotHints, setForgotHints] = useState(null);

  const onStudentLogin = async (e) => {
    e.preventDefault();
    setStuErr(""); setStuLoading(true);
    try {
      const res = await studentLogin(stuFirst.trim(), stuLast.trim(), stuId.trim().toUpperCase());
      if (res.first_login) {
        setReveal({ student_id: res.user?.student_id || stuId.trim().toUpperCase(), user: res.user });
      } else {
        nav("/dashboard", { replace: true });
      }
    } catch (e) { setStuErr(e.message); }
    finally { setStuLoading(false); }
  };

  const finishReveal = () => {
    if (reveal?.user) setUser(reveal.user);
    nav("/dashboard", { replace: true });
  };

  const onForgotId = async (e) => {
    e.preventDefault();
    setStuErr(""); setForgotHints(null);
    try {
      const { data } = await api.post("/auth/student-forgot-id", {
        first_name: stuFirst.trim(), last_name: forgotLast.trim(),
      });
      setForgotHints(data);
    } catch (e) { setStuErr(formatApiError(e)); }
  };

  return (
    <div className="card border-dashed" data-testid="school-student-card">
      {reveal ? (
        <div data-testid="student-id-reveal">
          <div className="flex items-center gap-2 mb-1">
            <IdentificationCard size={22} weight="duotone" color="#CCFF00" />
            <h3 className="text-xl font-bold font-display">{t("Here is your Student ID")}</h3>
          </div>
          <p className="text-[#000000] text-sm mb-3">
            Write it down and keep it safe — <strong>from now on this is your password.</strong> It's okay to take a screenshot.
          </p>
          <div className="rounded-xl bg-[#0A0D14] text-center py-5 mb-3">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#94A3B8]">{t("Your ID")}</div>
            <div className="text-3xl font-mono font-bold tracking-[0.3em] text-[#CCFF00]" data-testid="reveal-student-id">{reveal.student_id}</div>
          </div>
          <button data-testid="reveal-continue" className="btn-primary w-full" onClick={finishReveal}>
            I wrote it down — continue
          </button>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-1">
            <IdentificationCard size={22} weight="duotone" color="#CCFF00" />
            <h3 className="text-xl font-bold font-display">Classroom Student</h3>
          </div>
          <p className="text-[#000000] text-sm mb-4">
            Log in with your first and last name and the Student ID your teacher gave you.
          </p>
          <form onSubmit={onStudentLogin} className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-2">
              <input
                data-testid="student-firstname-input"
                className="input-base"
                value={stuFirst} onChange={(e) => setStuFirst(e.target.value)}
                placeholder={t("First name")} maxLength={60} required
              />
              <input
                data-testid="student-lastname-input"
                className="input-base"
                value={stuLast} onChange={(e) => setStuLast(e.target.value)}
                placeholder={t("Last name")} maxLength={60}
              />
            </div>
            <input
              data-testid="student-id-input"
              className="input-base font-mono tracking-[0.3em] uppercase text-center text-lg"
              value={stuId} onChange={(e) => setStuId(e.target.value.toUpperCase())}
              onPaste={(e) => e.preventDefault()}
              placeholder="ABC123" maxLength={10} required
            />
            {stuErr && <div data-testid="student-error" className="text-[#FF5100] text-sm">{stuErr}</div>}
            <button data-testid="student-login-submit" type="submit" className="btn-secondary" disabled={stuLoading}>
              {stuLoading ? t("Checking...") : t("Log in")}
            </button>
          </form>
          <button data-testid="forgot-id-toggle" className="btn-ghost text-[#000000] text-sm underline-offset-2 hover:underline mt-2"
            onClick={() => { setForgotOpen((v) => !v); setForgotHints(null); }}>
            {t("Forgot your ID?")}
          </button>
          {forgotOpen && (
            <form onSubmit={onForgotId} className="mt-2 flex gap-2 flex-wrap" data-testid="forgot-id-form">
              <input className="input-base flex-1" placeholder={t("Your first name")} value={stuFirst}
                onChange={(e) => setStuFirst(e.target.value)} data-testid="forgot-id-name" required />
              <input className="input-base flex-1" placeholder={t("Your last name")} value={forgotLast}
                onChange={(e) => setForgotLast(e.target.value)} data-testid="forgot-id-lastname" required />
              <button type="submit" className="btn-ghost text-sm border border-[#C4C8CE] rounded-lg px-3" data-testid="forgot-id-submit">{t("Find")}</button>
            </form>
          )}
          {forgotHints && (
            <div className="mt-2 text-sm text-[#000000]" data-testid="forgot-id-result">
              {forgotHints.matches?.length ? (
                <>
                  <p className="mb-1">{forgotHints.message}</p>
                  {forgotHints.matches.map((m, i) => (
                    <div key={i} className="font-mono text-[#000000]">{m.masked_id} <span className="text-[#000000]">· {m.grade} · {m.class_name}</span></div>
                  ))}
                </>
              ) : <p>{forgotHints.message}</p>}
            </div>
          )}
        </>
      )}
    </div>
  );
};
